import { KitType } from '@repo/types';
import { lowRelativeAbundanceLimit } from 'assets/constants';
import {
  initialValues,
  TaxonomyCategoryFilterType,
} from 'components/TaxonomyCategoryFilters';
import { capitalize } from 'helpers';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useMemo, useState } from 'react';
import { TaxaData } from 'services/microbiome/types/taxa-data.types';
import useTaxaData from 'services/microbiome/useTaxaData';

export default function useParseTaxaData() {
  const { currentKit, currentKitId } = useCurrentKitAndTinyAccount();
  const currentKitType = currentKit?.sampling_type;
  const [searchTerm, setSearchTerm] = useState('');
  const [taxonomyFilters, setTaxonomyFilters] =
    useState<TaxonomyCategoryFilterType[]>(initialValues);
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);

  const { data: rawData } = useTaxaData({
    kitType: currentKitType ?? KitType.STOOL,
    kitId: currentKitId,
    relativeAbundanceLimit: lowRelativeAbundanceLimit,
  });

  const groupedVaginalData = useMemo((): TaxaData[] => {
    if (!rawData) return [];

    const isVaginal = currentKitType === KitType.VAGINAL;

    // Not vaginal => do not group at all
    if (!isVaginal) return rawData;

    const groups: Record<string, TaxaData> = {};

    for (const item of rawData) {
      const itemKey = `${item.taxonomy_category ?? 'unknown'}:${item.tn ?? item.taxonomy_name ?? item.id}`;

      const currentGrouping = groups[itemKey];

      if (!currentGrouping) {
        groups[itemKey] = item;
        continue;
      }

      groups[itemKey] = {
        ...currentGrouping,
        classified_relative_abundance:
          (currentGrouping.classified_relative_abundance ?? 0) +
          (item.classified_relative_abundance ?? 0),
      };
    }

    return Object.values(groups);
  }, [rawData, currentKitType]);

  const dataAfterSearchAndTaxonomyFilters = useMemo(() => {
    if (!groupedVaginalData) return [];

    return groupedVaginalData.filter(bacterium => {
      // Search term filtering logic
      const loweredSearchTerm = searchTerm.toLowerCase();
      const taxonomyMatch = (bacterium.taxonomy_name ?? '')
        .toLowerCase()
        .includes(loweredSearchTerm);
      const tnMatch = (bacterium.tn ?? '')
        .toLowerCase()
        .includes(loweredSearchTerm);

      // Taxonomy category filtering logic
      const activeRankFilter = taxonomyFilters.find(f => f.active);
      const rankConditionMet = activeRankFilter
        ? activeRankFilter.value === '' || // 'All' is selected
          bacterium.taxonomy_category === activeRankFilter.value
        : true; // Default to true if no active filter

      return (taxonomyMatch || tnMatch) && rankConditionMet;
    });
  }, [groupedVaginalData, searchTerm, taxonomyFilters]);

  const filteredData = useMemo(() => {
    if (!dataAfterSearchAndTaxonomyFilters) {
      return [];
    }
    return dataAfterSearchAndTaxonomyFilters.filter(bacterium => {
      // Category filtering logic
      const categoryValue =
        currentKitType === KitType.VAGINAL
          ? bacterium.category_vaginal
          : bacterium.category;

      let categoryKeyForFilterCheck: string;
      if (
        categoryValue === undefined ||
        categoryValue === null ||
        (typeof categoryValue === 'string' && categoryValue.trim() === '')
      ) {
        categoryKeyForFilterCheck = 'Unknown';
      } else {
        categoryKeyForFilterCheck = capitalize(categoryValue as string);
      }
      return categoryFilters.includes(categoryKeyForFilterCheck);
    });
  }, [dataAfterSearchAndTaxonomyFilters, categoryFilters, currentKitType]);

  return {
    filteredData,
    dataAfterSearchAndTaxonomyFilters,
    searchTerm,
    setSearchTerm,
    taxonomyFilters,
    setTaxonomyFilters,
    categoryFilters,
    setCategoryFilters,
    currentKit,
  };
}
